import React, { useState, useCallback, useRef, useEffect } from 'react';
import Modal from '@app/react-common/components/modal/Modal';
import Button from '@app/react-common/components/button/Button';
import { NameDescriptionEditView, NameDescriptionFormData } from './views/NameDescriptionEditView';
import { NameDescriptionPreviewView } from './views/NameDescriptionPreviewView';
import { ShippingThresholdsEditView, ShippingThresholdsFormData } from './views/ShippingThresholdsEditView';
import { ShippingThresholdsPreviewView } from './views/ShippingThresholdsPreviewView';
import { ShippingRatesEditView, ShippingRatesFormData } from './views/ShippingRatesEditView';
import { ShippingRatesPreviewView } from './views/ShippingRatesPreviewView';
import { ShipToEditView, ShipToFormData } from './views/ShipToEditView';
import { ShipToPreviewView } from './views/ShipToPreviewView';
import { DisplayConditionsEditView } from './views/DisplayConditionsEditView';
import { DisplayConditionsPreviewView } from './views/DisplayConditionsPreviewView';
import { getCurrencySign } from '../components';
import { useCheckoutApi } from '@app/react-common/api/useCheckoutApi';
import { useAppContext } from '@app/react-common/AppContext';
import { ConflictResolutionView, UnsupportedModeView, type ConflictGroup } from './components';
import {
  useBulkEditConflicts,
  MODE_GROUP_LABELS,
  SPECIFIC_METHOD_LABELS,
  type MethodType,
} from './hooks/useBulkEditConflicts';
import { Shippings } from '@app/pages/checkout-app/types';
import { parseConditionKey } from './utils/conditionUtils';
import { BULK_EDIT_API_ENDPOINT, BULK_EDIT_MODES, DISPLAY_CONDITIONS_ACTIONS, NOTE_ACTIONS } from './utils/consts';
import { isInternationalMethod } from './utils/bulkEditUtils';
import { MoveToSectionTarget, MoveToSectionView } from './views/MoveToSectionView';
import { DeliveryMethodSection } from '../sections/deliveryMethodDisplayOrder';

export type BulkEditMode =
  | 'name-description'
  | 'shipping-thresholds'
  | 'shipping-rates'
  | 'ship-to'
  | 'display-conditions'
  | 'move-to-section';

interface EditModeOption {
  id: BulkEditMode;
  title: string;
  description: string;
}

const MOVE_TO_SECTION_MODE_OPTION: EditModeOption = {
  id: BULK_EDIT_MODES.MOVE_TO_SECTION,
  title: 'Move to section',
  description: 'Organize methods into sections',
};

const EDIT_MODE_OPTIONS: EditModeOption[] = [
  { id: BULK_EDIT_MODES.NAME_DESCRIPTION, title: 'Name & Description', description: 'Method name and note' },
  MOVE_TO_SECTION_MODE_OPTION,
  {
    id: BULK_EDIT_MODES.SHIPPING_THRESHOLDS,
    title: 'Shipping Thresholds',
    description: 'Adjust cart value thresholds',
  },
  { id: BULK_EDIT_MODES.SHIPPING_RATES, title: 'Shipping Rates', description: 'Shipping costs' },
  { id: BULK_EDIT_MODES.SHIP_TO, title: 'Ship To', description: 'States, cities, zones, and zip codes' },
  {
    id: BULK_EDIT_MODES.DISPLAY_CONDITIONS,
    title: 'Display Conditions',
    description: 'Customer tags, product tags, cart rules',
  },
];

export const SUPPORTED_DELIVERY_AREA_METHODS = ['all_cities', 'listed-states', 'zones', 'zip_codes'] as const;

export interface PriceRangeTier {
  minOrderPriceRange: number | null;
  maxOrderPriceRange: number | null;
  rate: number;
  hasDiscount?: boolean;
}

export interface PolygonInputValues {
  listedStates?: string[];
  listedStatesControl?: Record<string, string[]>;
  listedCities?: string[];
  zonesList?: Array<{ label: string; value: boolean }>;
  zipCodes?: string;
  zipCodesOperator?: 'includes' | 'excludes';
  excludedStates?: string[];
}

export interface InternationalSettings {
  countriesAndRegions?: Record<string, string[]>;
}

export interface ConditionInfo {
  id?: number;
  name: string;
  displayName: string;
  condition: string;
  values: string;
  weightUnit?: string;
  optionalValue?: string;
  subsetGroupId?: number;
}

export interface ConditionGroup {
  conditions: ConditionInfo[];
}

export interface DisplayConditionsFormData {
  action: 'add' | 'remove' | 'append-to-all';
  conditionGroups: ConditionGroup[];
  conditionsToRemove: string[];
}

export interface SelectedMethodInfo {
  id: string;
  shippingMethodId?: number;
  name: string;
  groupName: string;
  currency: string;
  note: string;
  minOrderPriceRange?: number | null;
  maxOrderPriceRange?: number | null;
  rate?: number;
  additionalPriceRanges?: PriceRangeTier[];
  shippingMethodCategory?: string;
  polygonInputValues?: PolygonInputValues[];
  internationalSettings?: InternationalSettings;
  deliveryAreaSelectMethod?: string;
  hasDiscount?: boolean;
  conditions?: ConditionInfo[];
  excludedStates?: string[];
}

export type DeliveryAreaModeGroup = 'states' | 'cities' | 'zones' | 'zip_codes' | 'all_areas' | 'custom';

interface BulkEditModalProps {
  isOpen: boolean;
  selectedCount: number;
  onClose: () => void;
  onSaveComplete?: (affectedGroupIds?: number[]) => void;
  onDeselectMethods?: (methodIds: string[]) => void;
  onRestoreMethods?: (methodIds: string[]) => void;
  currencySign?: string;
  selectedMethods?: SelectedMethodInfo[];
  shopCountryCode?: string;
  allShippingMethods?: Shippings[];
  moveToSection?: {
    sections: DeliveryMethodSection[];
    onMoveToSection: (target: MoveToSectionTarget) => void;
  };
}

interface BulkEditResultItem {
  id: number;
  success: boolean;
  shippingMethodGroupId?: number;
}

export const BulkEditModal = ({
  isOpen,
  selectedCount,
  onClose,
  onSaveComplete,
  onDeselectMethods,
  onRestoreMethods,
  currencySign = '$',
  selectedMethods = [],
  shopCountryCode = '',
  allShippingMethods = [],
  moveToSection,
}: BulkEditModalProps) => {
  const [selectedMode, setSelectedMode] = useState<BulkEditMode | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [nameDescriptionFormData, setNameDescriptionFormData] = useState<NameDescriptionFormData | null>(null);
  const [shippingThresholdsFormData, setShippingThresholdsFormData] = useState<ShippingThresholdsFormData | null>(null);
  const [shippingRatesFormData, setShippingRatesFormData] = useState<ShippingRatesFormData | null>(null);
  const [shipToFormData, setShipToFormData] = useState<ShipToFormData | null>(null);
  const [displayConditionsFormData, setDisplayConditionsFormData] = useState<DisplayConditionsFormData | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const initialMethodIdsRef = useRef<string[]>([]);

  useEffect(() => {
    if (isOpen) {
      initialMethodIdsRef.current = selectedMethods.map((m) => m.id);
    }
  }, [isOpen]);

  const { showToast, showFullPageLoader, refreshNgRxShippings } = useAppContext();

  // Use the conflicts hook for all conflict-related state and handlers
  const {
    currencyConflict,
    methodTypeConflict,
    deliveryAreaModeConflict,
    specificDeliveryMethodConflict,
    zipCodesOperatorConflict,
    liveRateConflict,
    methodsByCurrency,
    methodsByType,
    methodsByDeliveryAreaMode,
    methodsByZipOperator,
    methodsBySpecificDeliveryMethod,
    getResolvedDeliveryAreaMode,
    resetConflicts,
    checkAndSetConflictsForMode,
    handleSelectLiveRateMethods,
    handleSelectNonLiveRateMethods,
    handleSelectCurrency,
    handleSelectMethodType,
    handleSelectDeliveryAreaMode,
    handleSelectSpecificDeliveryMethod,
    handleSelectZipCodesOperator,
  } = useBulkEditConflicts({
    selectedMethods,
    onDeselectMethods,
  });

  const failureHandler = useCallback(
    (response: { message?: string }, status: number) => {
      console.error('Bulk edit API error:', response?.message, status);
      showToast({ message: 'Failed to save changes. Please try again.', mode: 'danger' });
      setIsSaving(false);
      showFullPageLoader(false);
    },
    [showToast, showFullPageLoader],
  );

  const checkoutApi = useCheckoutApi(failureHandler);

  const effectiveCurrencySign = selectedMethods[0]?.currency
    ? getCurrencySign(selectedMethods[0].currency)
    : currencySign;

  const handleClose = () => {
    if (onRestoreMethods && initialMethodIdsRef.current.length > 0) {
      onRestoreMethods(initialMethodIdsRef.current);
    }
    setSelectedMode(null);
    resetConflicts();
    setShowPreview(false);
    setNameDescriptionFormData(null);
    setShippingThresholdsFormData(null);
    setShippingRatesFormData(null);
    setShipToFormData(null);
    setDisplayConditionsFormData(null);
    onClose();
  };

  const handleBack = () => {
    if (showPreview) {
      setShowPreview(false);
      return;
    }
    if (onRestoreMethods && initialMethodIdsRef.current.length > 0) {
      onRestoreMethods(initialMethodIdsRef.current);
    }
    setSelectedMode(null);
    resetConflicts();
  };

  const handleSelectMode = (mode: BulkEditMode) => {
    checkAndSetConflictsForMode(mode);
    setSelectedMode(mode);
  };

  const handleSaveSuccess = async (response: {
    successCount: number;
    failureCount: number;
    totalProcessed: number;
    results?: BulkEditResultItem[];
  }) => {
    if (response.failureCount > 0) {
      showToast({
        message: `Updated ${response.successCount} of ${response.totalProcessed} methods. ${response.failureCount} failed.`,
        mode: 'warning',
      });
    } else {
      showToast({
        message: `Successfully updated ${response.successCount} delivery method${response.successCount !== 1 ? 's' : ''}.`,
        mode: 'success',
      });
    }
    try {
      await refreshNgRxShippings();
    } catch (error) {
      console.error('Failed to refresh shippings:', error);
    }
    if (onSaveComplete) {
      const affectedGroupIds = response.results
        ? [
            ...new Set(
              response.results.filter((r) => r.success && r.shippingMethodGroupId).map((r) => r.shippingMethodGroupId!),
            ),
          ]
        : [];
      onSaveComplete(affectedGroupIds);
    }
    initialMethodIdsRef.current = [];
    handleClose();
  };

  const handleNameDescriptionPreview = (data: NameDescriptionFormData) => {
    setNameDescriptionFormData(data);
    setShowPreview(true);
  };

  const handleNameDescriptionSave = async (affectedIds: string[]) => {
    if (!nameDescriptionFormData || isSaving || affectedIds.length === 0) return;
    setIsSaving(true);
    showFullPageLoader(true);
    try {
      const ids = affectedIds.map((id) => parseInt(id, 10));
      const nameDescriptionData: { name?: string; notes?: string } = {};
      if (nameDescriptionFormData.name.trim()) {
        nameDescriptionData.name = nameDescriptionFormData.name;
      }
      if (nameDescriptionFormData.noteAction === 'change' && nameDescriptionFormData.note.trim()) {
        nameDescriptionData.notes = nameDescriptionFormData.note;
      } else if (nameDescriptionFormData.noteAction === NOTE_ACTIONS.REMOVE) {
        nameDescriptionData.notes = '';
      }
      const response = await checkoutApi.post(BULK_EDIT_API_ENDPOINT, {
        mode: BULK_EDIT_MODES.NAME_DESCRIPTION,
        ids,
        nameDescriptionData,
      });
      await handleSaveSuccess(response.data);
    } catch (error) {
      console.error('Failed to save bulk edit changes:', error);
    } finally {
      setIsSaving(false);
      showFullPageLoader(false);
    }
  };

  const handleShippingThresholdsPreview = (data: ShippingThresholdsFormData) => {
    setShippingThresholdsFormData(data);
    setShowPreview(true);
  };

  const handleShippingThresholdsSave = async (affectedIds: string[]) => {
    if (!shippingThresholdsFormData || isSaving || affectedIds.length === 0) return;
    setIsSaving(true);
    showFullPageLoader(true);
    try {
      const ids = affectedIds.map((id) => parseInt(id, 10));
      const response = await checkoutApi.post(BULK_EDIT_API_ENDPOINT, {
        mode: BULK_EDIT_MODES.SHIPPING_THRESHOLDS,
        ids,
        shippingThresholdsData: {
          adjustBy: shippingThresholdsFormData.adjustBy,
          keepZeroAsMinimum: shippingThresholdsFormData.keepZeroAsMinimum,
        },
      });
      await handleSaveSuccess(response.data);
    } catch (error) {
      console.error('Failed to save bulk edit changes:', error);
    } finally {
      setIsSaving(false);
      showFullPageLoader(false);
    }
  };

  const handleShippingRatesPreview = (data: ShippingRatesFormData) => {
    setShippingRatesFormData(data);
    setShowPreview(true);
  };

  const handleShippingRatesSave = async (affectedIds: string[]) => {
    if (!shippingRatesFormData || isSaving || affectedIds.length === 0) return;
    setIsSaving(true);
    showFullPageLoader(true);
    try {
      const ids = affectedIds.map((id) => parseInt(id, 10));
      const response = await checkoutApi.post(BULK_EDIT_API_ENDPOINT, {
        mode: BULK_EDIT_MODES.SHIPPING_RATES,
        ids,
        shippingRatesData: {
          adjustBy: shippingRatesFormData.adjustBy,
          keepFreeShippingFree: shippingRatesFormData.keepFreeShippingFree,
        },
      });
      await handleSaveSuccess(response.data);
    } catch (error) {
      console.error('Failed to save bulk edit changes:', error);
    } finally {
      setIsSaving(false);
      showFullPageLoader(false);
    }
  };

  const handleShipToPreview = (data: ShipToFormData) => {
    setShipToFormData(data);
    setShowPreview(true);
  };

  const handleShipToSave = async (affectedIds: string[]) => {
    if (!shipToFormData || isSaving || affectedIds.length === 0) return;
    setIsSaving(true);
    showFullPageLoader(true);
    try {
      const ids = affectedIds.map((id) => parseInt(id, 10));
      const response = await checkoutApi.post(BULK_EDIT_API_ENDPOINT, {
        mode: BULK_EDIT_MODES.SHIP_TO,
        ids,
        shipToData: {
          action: shipToFormData.action,
          shopCountryCode: shipToFormData.shopCountryCode,
          statesControl: shipToFormData.statesControl,
          zones: shipToFormData.zones,
          zipCodes: shipToFormData.zipCodes,
          excludedStates: shipToFormData.excludedStates,
        },
      });
      await handleSaveSuccess(response.data);
    } catch (error) {
      console.error('Failed to save bulk edit changes:', error);
    } finally {
      setIsSaving(false);
      showFullPageLoader(false);
    }
  };

  const handleDisplayConditionsPreview = (data: DisplayConditionsFormData) => {
    setDisplayConditionsFormData(data);
    setShowPreview(true);
  };

  const handleDisplayConditionsSave = async (affectedIds: string[]) => {
    if (!displayConditionsFormData || isSaving || affectedIds.length === 0) return;
    setIsSaving(true);
    showFullPageLoader(true);
    try {
      const ids = affectedIds.map((id) => parseInt(id, 10));
      const conditionsToRemove = displayConditionsFormData.conditionsToRemove
        .map((key) => parseConditionKey(key))
        .filter((c) => c.name && c.condition && c.values);

      const response = await checkoutApi.post(BULK_EDIT_API_ENDPOINT, {
        mode: BULK_EDIT_MODES.DISPLAY_CONDITIONS,
        ids,
        displayConditionsData: {
          action: displayConditionsFormData.action,
          conditionGroups:
            displayConditionsFormData.action === DISPLAY_CONDITIONS_ACTIONS.ADD ||
            displayConditionsFormData.action === DISPLAY_CONDITIONS_ACTIONS.APPEND_TO_ALL
              ? displayConditionsFormData.conditionGroups
              : undefined,
          conditionsToRemove:
            displayConditionsFormData.action === DISPLAY_CONDITIONS_ACTIONS.REMOVE ? conditionsToRemove : undefined,
        },
      });
      await handleSaveSuccess(response.data);
    } catch (error) {
      console.error('Failed to save bulk edit changes:', error);
    } finally {
      setIsSaving(false);
      showFullPageLoader(false);
    }
  };

  const renderContent = (): React.ReactNode => {
    // Live rate conflict resolution
    if (
      liveRateConflict &&
      (selectedMode === BULK_EDIT_MODES.SHIPPING_THRESHOLDS || selectedMode === BULK_EDIT_MODES.SHIPPING_RATES)
    ) {
      const modeTitle = selectedMode === BULK_EDIT_MODES.SHIPPING_THRESHOLDS ? 'Shipping Thresholds' : 'Shipping Rates';
      const allAreLiveRate = liveRateConflict.nonLiveRateMethods.length === 0;

      if (allAreLiveRate) {
        return (
          <UnsupportedModeView
            title={modeTitle}
            featureName={selectedMode === BULK_EDIT_MODES.SHIPPING_THRESHOLDS ? 'thresholds' : 'rates'}
            onBack={handleBack}
            onClose={handleClose}
          />
        );
      }

      const groups: ConflictGroup<'live' | 'fixed'>[] = [
        { key: 'live', label: 'Live Rate Methods', methods: liveRateConflict.liveRateMethods },
        { key: 'fixed', label: 'Fixed Rate Methods', methods: liveRateConflict.nonLiveRateMethods },
      ];

      return (
        <ConflictResolutionView
          title={modeTitle}
          infoMessage="Select which method type to proceed with. Other methods will be deselected."
          groups={groups}
          onSelect={(key) => (key === 'live' ? handleSelectLiveRateMethods() : handleSelectNonLiveRateMethods())}
          onBack={handleBack}
          onClose={handleClose}
        />
      );
    }

    // Currency conflict resolution
    if (
      currencyConflict &&
      (selectedMode === BULK_EDIT_MODES.SHIPPING_THRESHOLDS || selectedMode === BULK_EDIT_MODES.SHIPPING_RATES)
    ) {
      const modeTitle = selectedMode === BULK_EDIT_MODES.SHIPPING_THRESHOLDS ? 'Shipping Thresholds' : 'Shipping Rates';
      const groups: ConflictGroup<string>[] = Object.entries(methodsByCurrency).map(([currency, methods]) => ({
        key: currency,
        label: `Currency: ${currency}`,
        methods,
      }));

      return (
        <ConflictResolutionView
          title={modeTitle}
          infoMessage="Select which currency group to proceed with. Other methods will be deselected."
          groups={groups}
          onSelect={handleSelectCurrency}
          onBack={handleBack}
          onClose={handleClose}
        />
      );
    }

    // Method type conflict resolution (Ship To)
    if (methodTypeConflict && selectedMode === BULK_EDIT_MODES.SHIP_TO) {
      const groups: ConflictGroup<MethodType>[] = [
        { key: 'domestic', label: 'Domestic Methods', methods: methodsByType.domestic },
        { key: 'international', label: 'International Methods', methods: methodsByType.international },
      ];

      return (
        <ConflictResolutionView
          title="Ship To"
          infoMessage="Select which method type to proceed with. Other methods will be deselected."
          groups={groups}
          onSelect={handleSelectMethodType}
          onBack={handleBack}
          onClose={handleClose}
        />
      );
    }

    // Delivery area mode conflict resolution
    if (deliveryAreaModeConflict && selectedMode === BULK_EDIT_MODES.SHIP_TO) {
      const groups: ConflictGroup<DeliveryAreaModeGroup>[] = (
        Object.entries(methodsByDeliveryAreaMode) as [DeliveryAreaModeGroup, SelectedMethodInfo[]][]
      )
        .filter(([, methods]) => methods.length > 0)
        .map(([modeGroup, methods]) => ({ key: modeGroup, label: MODE_GROUP_LABELS[modeGroup], methods }));

      return (
        <ConflictResolutionView
          title="Ship To"
          infoMessage="Select which delivery area type to proceed with. Other methods will be deselected."
          groups={groups}
          onSelect={handleSelectDeliveryAreaMode}
          onBack={handleBack}
          onClose={handleClose}
        />
      );
    }

    // Specific delivery method conflict resolution
    if (specificDeliveryMethodConflict && selectedMode === BULK_EDIT_MODES.SHIP_TO) {
      const groups: ConflictGroup<string>[] = Object.entries(methodsBySpecificDeliveryMethod)
        .filter(([, methods]) => methods.length > 0)
        .map(([specificMethod, methods]) => ({
          key: specificMethod,
          label: SPECIFIC_METHOD_LABELS[specificMethod] || specificMethod,
          methods,
        }));

      return (
        <ConflictResolutionView
          title="Ship To"
          infoMessage="Select which specific delivery method to proceed with. Other methods will be deselected."
          groups={groups}
          onSelect={handleSelectSpecificDeliveryMethod}
          onBack={handleBack}
          onClose={handleClose}
        />
      );
    }

    // Zip codes operator conflict resolution
    if (zipCodesOperatorConflict && selectedMode === BULK_EDIT_MODES.SHIP_TO) {
      const groups: ConflictGroup<'includes' | 'excludes'>[] = [
        { key: 'includes', label: 'Include Only (Whitelist)', methods: methodsByZipOperator.includes },
        { key: 'excludes', label: 'Exclude (Blacklist)', methods: methodsByZipOperator.excludes },
      ];

      return (
        <ConflictResolutionView
          title="Ship To"
          infoMessage="Select which zip code operator to proceed with. Other methods will be deselected."
          groups={groups}
          onSelect={handleSelectZipCodesOperator}
          onBack={handleBack}
          onClose={handleClose}
        />
      );
    }

    // Edit mode views
    if (selectedMode === BULK_EDIT_MODES.SHIPPING_THRESHOLDS) {
      if (showPreview && shippingThresholdsFormData) {
        return (
          <ShippingThresholdsPreviewView
            onBack={handleBack}
            onClose={handleClose}
            onSaveChanges={handleShippingThresholdsSave}
            formData={shippingThresholdsFormData}
            selectedMethods={selectedMethods}
            currencySign={effectiveCurrencySign}
          />
        );
      }
      return (
        <ShippingThresholdsEditView
          onBack={handleBack}
          onClose={handleClose}
          onPreviewChanges={handleShippingThresholdsPreview}
          currencySign={effectiveCurrencySign}
          selectedMethods={selectedMethods}
        />
      );
    }

    if (selectedMode === BULK_EDIT_MODES.SHIPPING_RATES) {
      if (showPreview && shippingRatesFormData) {
        return (
          <ShippingRatesPreviewView
            onBack={handleBack}
            onClose={handleClose}
            onSaveChanges={handleShippingRatesSave}
            formData={shippingRatesFormData}
            selectedMethods={selectedMethods}
            currencySign={effectiveCurrencySign}
          />
        );
      }
      return (
        <ShippingRatesEditView
          onBack={handleBack}
          onClose={handleClose}
          onPreviewChanges={handleShippingRatesPreview}
          currencySign={effectiveCurrencySign}
          selectedMethods={selectedMethods}
        />
      );
    }

    if (selectedMode === BULK_EDIT_MODES.SHIP_TO) {
      const allAreInternational = selectedMethods.every(isInternationalMethod);
      if (allAreInternational) {
        return (
          <UnsupportedModeView
            title="Ship To"
            featureName="ship to"
            methodType="international"
            onBack={handleBack}
            onClose={handleClose}
          />
        );
      }

      const resolvedDeliveryAreaMode = getResolvedDeliveryAreaMode();
      if (showPreview && shipToFormData) {
        return (
          <ShipToPreviewView
            onBack={handleBack}
            onClose={handleClose}
            onSaveChanges={handleShipToSave}
            formData={shipToFormData}
            selectedMethods={selectedMethods}
            deliveryAreaMode={resolvedDeliveryAreaMode}
          />
        );
      }
      return (
        <ShipToEditView
          onBack={handleBack}
          onClose={handleClose}
          onPreviewChanges={handleShipToPreview}
          selectedMethods={selectedMethods}
          shopCountryCode={shopCountryCode}
          deliveryAreaMode={resolvedDeliveryAreaMode}
        />
      );
    }

    if (selectedMode === BULK_EDIT_MODES.NAME_DESCRIPTION) {
      if (showPreview && nameDescriptionFormData) {
        return (
          <NameDescriptionPreviewView
            onBack={handleBack}
            onClose={handleClose}
            onSaveChanges={handleNameDescriptionSave}
            formData={nameDescriptionFormData}
            selectedMethods={selectedMethods}
          />
        );
      }
      return (
        <NameDescriptionEditView
          onBack={handleBack}
          onClose={handleClose}
          onPreviewChanges={handleNameDescriptionPreview}
          selectedMethods={selectedMethods}
          allShippingMethods={allShippingMethods}
        />
      );
    }

    if (selectedMode === BULK_EDIT_MODES.MOVE_TO_SECTION && moveToSection) {
      return (
        <MoveToSectionView
          sections={moveToSection.sections}
          onBack={handleBack}
          onClose={handleClose}
          onMoveToSection={(target) => {
            handleClose();
            moveToSection.onMoveToSection(target);
          }}
        />
      );
    }

    if (selectedMode === BULK_EDIT_MODES.DISPLAY_CONDITIONS) {
      if (showPreview && displayConditionsFormData) {
        return (
          <DisplayConditionsPreviewView
            onBack={handleBack}
            onClose={handleClose}
            onSaveChanges={handleDisplayConditionsSave}
            formData={displayConditionsFormData}
            selectedMethods={selectedMethods}
          />
        );
      }
      return (
        <DisplayConditionsEditView
          onBack={handleBack}
          onClose={handleClose}
          onPreviewChanges={handleDisplayConditionsPreview}
          selectedMethods={selectedMethods}
        />
      );
    }

    const availableEditModeOptions = moveToSection
      ? EDIT_MODE_OPTIONS
      : EDIT_MODE_OPTIONS.filter((editModeOption) => editModeOption.id !== BULK_EDIT_MODES.MOVE_TO_SECTION);

    // Initial mode selection view
    return (
      <>
        <div className="bulk-edit-modal__header">
          <h2>Bulk Edit {selectedCount} Methods</h2>
          <Button icon="close" variant="ghost" size="small" onClick={handleClose} />
        </div>
        <div className="bulk-edit-modal__content">
          {availableEditModeOptions.map((option) => (
            <button key={option.id} className="bulk-edit-modal__option" onClick={() => handleSelectMode(option.id)}>
              <span className="bulk-edit-modal__option__title">{option.title}</span>
              <span className="bulk-edit-modal__option__description">{option.description}</span>
            </button>
          ))}
        </div>
        <div className="bulk-edit-modal__footer">
          <Button text="Cancel" onClick={handleClose} size="medium" variant="secondary" />
        </div>
      </>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} defaultPadding={false}>
      <div className="bulk-edit-modal">{renderContent()}</div>
    </Modal>
  );
};
